import type { Vec2 } from './vec.js';
import { ensureCounterClockwise, signedArea, type Polygon } from './polygon.js';

/** Sharp corners would push the mitre point far out; beyond this factor the corner is cut. */
const MAX_MITRE = 4;

/**
 * Polygon moved inwards by `distance` along every edge (mitred corners), counter-clockwise.
 * For wall centrelines and clear floor areas of a hall with any outline.
 */
export function insetPolygon(poly: Polygon, distance: number): Vec2[] {
  const pts = ensureCounterClockwise(poly);
  const n = pts.length;
  // Inward normal of a counter-clockwise edge points to its left.
  const normal = (a: Vec2, b: Vec2): Vec2 => {
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
  };
  return pts.map((p, i) => {
    const prev = pts[(i - 1 + n) % n] as Vec2;
    const next = pts[(i + 1) % n] as Vec2;
    const n1 = normal(prev, p);
    const n2 = normal(p, next);
    const bis = { x: n1.x + n2.x, y: n1.y + n2.y };
    const dot = bis.x * n1.x + bis.y * n1.y;
    if (dot < 1e-9) return { x: p.x + n1.x * distance, y: p.y + n1.y * distance };
    // Mitre: along the bisector, so both offset edges meet in one point.
    const scale = Math.min(distance / dot, distance * MAX_MITRE);
    return { x: p.x + bis.x * scale, y: p.y + bis.y * scale };
  });
}

const cross = (o: Vec2, a: Vec2, b: Vec2) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

function insideTriangle(p: Vec2, a: Vec2, b: Vec2, c: Vec2): boolean {
  return cross(a, b, p) >= -1e-12 && cross(b, c, p) >= -1e-12 && cross(c, a, p) >= -1e-12;
}

/**
 * Splits a simple polygon (convex or not) into triangles by ear clipping; returns index
 * triples into the polygon's points, each triangle counter-clockwise.
 */
export function triangulatePolygon(poly: Polygon): [number, number, number][] {
  const ccw = signedArea(poly) > 0;
  const order = poly.map((_, i) => i);
  if (!ccw) order.reverse();
  const at = (i: number) => poly[i] as Vec2;
  const triangles: [number, number, number][] = [];
  let guard = 0;
  while (order.length > 3 && guard++ < 10_000) {
    let clipped = false;
    for (let k = 0; k < order.length; k++) {
      const ia = order[(k - 1 + order.length) % order.length] as number;
      const ib = order[k] as number;
      const ic = order[(k + 1) % order.length] as number;
      const a = at(ia);
      const b = at(ib);
      const c = at(ic);
      if (cross(a, b, c) <= 1e-12) continue;
      // Rings with holes joined by a bridge repeat points: a copy of a corner does not block.
      const corner = (p: Vec2) => [a, b, c].some((q) => Math.hypot(p.x - q.x, p.y - q.y) < 1e-9);
      const blocked = order.some(
        (j) =>
          j !== ia && j !== ib && j !== ic && !corner(at(j)) && insideTriangle(at(j), a, b, c),
      );
      if (blocked) continue;
      triangles.push([ia, ib, ic]);
      order.splice(k, 1);
      clipped = true;
      break;
    }
    // Degenerate rest (collinear points): stop instead of looping.
    if (!clipped) break;
  }
  if (order.length === 3)
    triangles.push([order[0] as number, order[1] as number, order[2] as number]);
  return triangles;
}
